import React, { useState } from "react";
import { Alert, Button, Form, Input, Space, Typography } from "antd";
import { confirmRecoveryPhone, enrolRecoveryPhone } from "../../../api/auth/mfaApi";
import { authFailure, authFailureMessage, fieldError, hasFieldError, isStepUpDeclined, type AuthFailure } from "../../../lib/http/authErrors";
import type { SmsEnrolment } from "../../../types/auth/mfa";
import OtpInput from "../../../components/auth/mfa/OtpInput";
import { isOtp, normaliseOtp } from "../../../components/auth/mfa/codes";
import EnrolmentEmailCode from "./EnrolmentEmailCode";

const { Paragraph } = Typography;

const toE164 = (value: string): string => value.replace(/[\s()-]/g, "");

const BAD_EMAIL_CODE = "The email code is incorrect or has expired.";

const isEmailCodeRequired = (failure: AuthFailure): boolean => failure.code === "mfa_enrolment_required" && failure.details.reason === "email_code_required";

interface SmsRecoverySetupProps {
	requireEmailCode: boolean;
	onDone: () => void;
	onCancel: () => void;
}

// The recovery phone (contract §6.6): SMS is a way back in after losing a device, never a sign-in factor.
const SmsRecoverySetup: React.FC<SmsRecoverySetupProps> = ({ requireEmailCode, onDone, onCancel }) => {
	const [enrolment, setEnrolment] = useState<SmsEnrolment | null>(null);
	const [emailRequired, setEmailRequired] = useState(requireEmailCode);
	// sms/enrol checks the email code without spending it; sms/confirm must send the same one (1.2.1).
	const [emailCode, setEmailCode] = useState<string | undefined>(undefined);
	const [askEmailAgain, setAskEmailAgain] = useState(false);
	const [busy, setBusy] = useState(false);
	const [error, setError] = useState("");
	const [phoneForm] = Form.useForm<{ phone: string; email_code?: string }>();
	const [codeForm] = Form.useForm<{ code: string; email_code?: string }>();

	const emailNotRequired = () => {
		setEmailRequired(false);
		setAskEmailAgain(false);
		setEmailCode(undefined);
	};

	const sendCode = async (values: { phone: string; email_code?: string }) => {
		setBusy(true);
		setError("");
		const code = emailRequired ? normaliseOtp(values.email_code ?? "") : undefined;
		try {
			setEnrolment(await enrolRecoveryPhone(toE164(values.phone), code));
			setEmailCode(code);
			setAskEmailAgain(false);
		} catch (err) {
			const failure = authFailure(err);
			if (failure.status === 422 && hasFieldError(failure, "phone")) {
				phoneForm.setFields([{ name: "phone", errors: [fieldError(failure, "phone") ?? "Enter a mobile number in international format, like +61 412 345 678."] }]);
			} else if (failure.status === 422 && hasFieldError(failure, "email_code")) {
				phoneForm.setFields([{ name: "email_code", errors: [BAD_EMAIL_CODE] }]);
			} else if (isEmailCodeRequired(failure)) {
				setEmailRequired(true);
				setError("Enter the code we email you to continue.");
			} else if (!isStepUpDeclined(err)) {
				setError(authFailureMessage(err));
			}
		} finally {
			setBusy(false);
		}
	};

	const confirm = async (values: { code: string; email_code?: string }) => {
		if (!enrolment) return;
		setBusy(true);
		setError("");
		try {
			const confirmEmailCode = askEmailAgain && emailRequired ? normaliseOtp(values.email_code ?? "") : emailCode;
			await confirmRecoveryPhone(enrolment.factor_id, normaliseOtp(values.code), confirmEmailCode);
			onDone();
		} catch (err) {
			const failure = authFailure(err);
			if (failure.status === 422 && hasFieldError(failure, "code")) {
				codeForm.setFields([{ name: "code", value: "", errors: ["That code is incorrect."] }]);
			} else if (failure.status === 422 && hasFieldError(failure, "email_code")) {
				// identity keeps the texted code usable, so only the email code is asked for again.
				if (askEmailAgain) codeForm.setFields([{ name: "email_code", errors: [BAD_EMAIL_CODE] }]);
				else setError(`${BAD_EMAIL_CODE} Request a new one to finish.`);
				setAskEmailAgain(true);
			} else if (isEmailCodeRequired(failure)) {
				setEmailRequired(true);
				setAskEmailAgain(true);
				setError("Enter the code we email you to finish.");
			} else if (failure.status === 404) {
				setEnrolment(null);
				setError("That code expired. Send a new one.");
			} else if (!isStepUpDeclined(err)) {
				setError(authFailureMessage(err));
			}
		} finally {
			setBusy(false);
		}
	};

	return (
		<div data-testid="security-sms-setup">
			<Paragraph type="secondary">Used only to text you a code if you lose your authenticator app or passkey. It is never used to sign in.</Paragraph>
			{error && <Alert data-testid="security-sms-error" type="error" showIcon message={error} style={{ marginBottom: "var(--mg-sp-6)" }} />}
			{/* Distinct keys: one reused <Form> keeps its first store, so codeForm.setFields would never reach the code field. */}
			{!enrolment ? (
				<Form key="sms-phone" form={phoneForm} name="sms-phone" layout="vertical" onFinish={sendCode} requiredMark={false}>
					<Form.Item
						name="phone"
						label="Mobile number"
						extra="International format, like +61 412 345 678"
						rules={[{ validator: (_, value) => (/^\+[1-9]\d{6,14}$/.test(toE164(value ?? "")) ? Promise.resolve() : Promise.reject(new Error("Enter the number with its country code, like +61 412 345 678"))) }]}
					>
						<Input data-testid="security-sms-phone" type="tel" inputMode="tel" autoComplete="tel" disabled={busy} />
					</Form.Item>
					{emailRequired && <EnrolmentEmailCode onNotRequired={emailNotRequired} disabled={busy} />}
					<Space wrap>
						<Button data-testid="security-sms-send" type="primary" htmlType="submit" loading={busy}>
							Text me a code
						</Button>
						<Button onClick={onCancel} disabled={busy}>
							Cancel
						</Button>
					</Space>
				</Form>
			) : (
				<Form key="sms-code" form={codeForm} name="sms-code" layout="vertical" onFinish={confirm} requiredMark={false}>
					<Paragraph data-testid="security-sms-sent">Code sent to {enrolment.masked_destination}.</Paragraph>
					<Form.Item
						name="code"
						label="6-digit code"
						rules={[{ validator: (_, value) => (isOtp(value ?? "") ? Promise.resolve() : Promise.reject(new Error("Enter the 6-digit code"))) }]}
					>
						<OtpInput data-testid="security-sms-code" disabled={busy} autoFocus />
					</Form.Item>
					{askEmailAgain && emailRequired && <EnrolmentEmailCode onNotRequired={emailNotRequired} disabled={busy} />}
					<Space wrap>
						<Button data-testid="security-sms-confirm" type="primary" htmlType="submit" loading={busy}>
							Save recovery phone
						</Button>
						<Button onClick={() => setEnrolment(null)} disabled={busy}>
							Change number
						</Button>
					</Space>
				</Form>
			)}
		</div>
	);
};

export default SmsRecoverySetup;
